import { useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { ArrowDown, ArrowUp, Loader2, Minus, Plus, Repeat, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import ExercisePicker from "./ExercisePicker";
import ItemNotices from "./ItemNotices";
import { planLine } from "./lib/format";
import {
  MAX_ITEMS,
  MAX_SETS,
  MIN_SETS,
  addItem,
  addOptions,
  loggedCount,
  moveAt,
  remainingSets,
  removeAt,
  setSetsAt,
  swapAt,
  swapOptions,
} from "./lib/planEdit";

/**
 * Change a plan (walkthrough 4) — before a session (Today's plan) or during
 * one (the session view): swap an exercise for one that trains the same
 * muscles with your equipment (best first), add one from the catalog,
 * remove one, change its set count (1–10), move it up or down. Every change
 * hands `onChange` a NEW items array; the caller saves it (a draft before
 * Start, a session PATCH after).
 *
 * A swap or an add goes in at once with numbers made here (itemFor: find
 * your weight) and `onChange(items, brought)` says which item that is —
 * `{item, exercise, slot, sets}` — so the caller can ask the engine for its
 * real ones (W3). `notes[exercise key]` (`{getting, failed, notices}`) is
 * what the caller has to say about it: "Getting its numbers…" (its set
 * count waits for them), that they couldn't be got, and the engine's
 * notices, beside the row.
 *
 * `logged` (`{exercise key: sets logged}`, during a session) protects what's
 * done: a swap moves only the sets still to do — the logged ones stay with
 * their exercise and a new item takes the rest — a set count never drops
 * below the sets logged, and removing an exercise with logged sets drops
 * only the sets still to do. Each exercise appears once in a plan (so a
 * count per key is unambiguous): swaps and adds offer only what isn't in
 * it yet.
 */
function PlanEditor({ items, exercises, settings, onChange, disabled = false, logged = {}, units, notes = {} }) {
  const unit = units ?? settings?.units ?? "lb";
  const byKey = useMemo(() => new Map((exercises ?? []).map((e) => [e.key, e])), [exercises]);
  const inPlan = useMemo(() => items.map((item) => item.exercise), [items]);
  // The open picker: {mode: "swap", index} or {mode: "add"}; null when closed.
  const [picker, setPicker] = useState(null);
  const listRef = useRef(null);
  const addRef = useRef(null);
  // Where focus goes once the picker closes after a pick (the row it made).
  const focusRow = useRef(null);

  // Focus the first enabled match once React has committed the change — a
  // row that moved, or the one after a removed row. A focused button that
  // goes away (or turns disabled) would leave a keyboard user on <body>.
  const focusIn = (selectors, fallback) => {
    setTimeout(() => {
      for (const selector of selectors) {
        const target = listRef.current?.querySelector(selector);
        if (target && !target.disabled) {
          target.focus();
          return;
        }
      }
      fallback?.focus();
    }, 0);
  };
  const control = (key, action) => `[data-row="${key}"] [data-action="${action}"]`;

  const nameOf = (item) => item.name ?? byKey.get(item.exercise)?.name ?? item.exercise;
  const swapping = picker?.mode === "swap" ? items[picker.index] : null;
  const swapExercise = swapping ? byKey.get(swapping.exercise) : null;
  const choices = useMemo(() => {
    if (!picker) return { options: [], unavailable: [] };
    if (picker.mode === "add") return addOptions(exercises, settings, { exclude: inPlan });
    return swapOptions(swapExercise, exercises, settings, { exclude: inPlan });
  }, [picker, exercises, settings, inPlan, swapExercise]);

  const swapDescription = () => {
    if (!swapping) return "";
    const name = nameOf(swapping);
    if (!swapExercise) {
      return `${name} isn't in your exercise list any more, so there's nothing to match it against — remove it and add another instead.`;
    }
    const done = loggedCount(logged, swapping.exercise);
    const left = remainingSets(swapping, logged);
    const sets = `${left} ${left === 1 ? "set" : "sets"}`;
    if (done > 0) {
      return `Same muscles, with your equipment, best first. The new exercise takes the ${sets} still to do; the ${done} logged stay with ${name}. Its reps and weight come from your history, as the plan's do.`;
    }
    return `Same muscles, with your equipment, best first. Its sets, reps and weight come from your history, as the plan's do.`;
  };

  const pick = (exercise) => {
    const swap = picker?.mode === "swap";
    const slot = swap ? items[picker.index] : null;
    const next = swap ? swapAt(items, picker.index, exercise, logged) : addItem(items, exercise);
    const item = next === items ? null : next.find((it) => it.exercise === exercise.key);
    // after some sets, the new one takes the sets left (as the session does)
    const sets = slot && loggedCount(logged, slot.exercise) > 0 ? remainingSets(slot, logged) : undefined;
    onChange(next, item ? { item, exercise, slot, sets } : undefined);
    focusRow.current = exercise.key;
    setPicker(null);
  };

  const rowKeys = useMemo(() => {
    const seen = new Map();
    return items.map((item) => {
      const n = seen.get(item.exercise) ?? 0;
      seen.set(item.exercise, n + 1);
      return `${item.exercise}#${n}`;
    });
  }, [items]);

  const full = items.length >= MAX_ITEMS;

  return (
    <div className="flex flex-col gap-3">
      {items.length === 0 ? (
        <p className="m-0 rounded-lg border border-dashed bg-card p-4 text-sm text-muted-foreground">
          No exercises yet — add the first one below. Each gets its sets, reps and weight from your
          history, as a planned one would.
        </p>
      ) : (
        <ol
          ref={listRef}
          aria-label="Exercises to change"
          className="m-0 flex list-none flex-col gap-2 p-0"
        >
          {items.map((item, index) => {
            const name = nameOf(item);
            const exercise = byKey.get(item.exercise);
            const done = loggedCount(logged, item.exercise);
            const left = remainingSets(item, logged);
            const floor = Math.max(MIN_SETS, done);
            const removeLabel = done > 0 ? `Drop the sets left of ${name}` : `Remove ${name}`;
            const rowKey = rowKeys[index];
            const note = notes[item.exercise] ?? null;
            const getting = Boolean(note?.getting);
            return (
              <li
                key={rowKey}
                data-row={item.exercise}
                className="rounded-lg border bg-card p-4"
              >
                <div className="font-medium">{name}</div>
                {/* read out when its numbers arrive (W3) */}
                <div
                  aria-live={note ? "polite" : undefined}
                  className="text-sm tabular-nums text-muted-foreground"
                >
                  {getting ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                      Getting its numbers…
                    </span>
                  ) : (
                    planLine(item, unit, exercise)
                  )}
                </div>
                {note?.failed && (
                  <p className="mt-1 mb-0 text-sm text-foreground/80">
                    Couldn&apos;t get its numbers — your first set finds the weight.
                  </p>
                )}
                {note && note.notices.length > 0 && <ItemNotices notices={note.notices} />}
                {done > 0 && (
                  <p className="mt-1 mb-0 text-sm text-muted-foreground">
                    {done} of {item.sets} logged — they stay with {name}.
                  </p>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <div
                    role="group"
                    aria-label={`Sets of ${name}`}
                    className="flex items-center gap-1"
                  >
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="size-11 md:size-9"
                      aria-label={`Fewer sets of ${name}`}
                      title={`Fewer sets of ${name}`}
                      disabled={disabled || getting || item.sets <= floor}
                      onClick={() => onChange(setSetsAt(items, index, item.sets - 1, logged))}
                    >
                      <Minus aria-hidden="true" />
                    </Button>
                    {/* read out when − / + change it (a10) */}
                    <span
                      aria-live="polite"
                      aria-atomic="true"
                      className="min-w-14 text-center text-sm tabular-nums"
                    >
                      {item.sets} {item.sets === 1 ? "set" : "sets"}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="size-11 md:size-9"
                      aria-label={`More sets of ${name}`}
                      title={`More sets of ${name}`}
                      disabled={disabled || getting || item.sets >= MAX_SETS}
                      onClick={() => onChange(setSetsAt(items, index, item.sets + 1, logged))}
                    >
                      <Plus aria-hidden="true" />
                    </Button>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 md:h-9"
                    data-action="swap"
                    aria-label={`Swap ${name}`}
                    title={left > 0 ? `Swap ${name}` : `Every set of ${name} is logged`}
                    disabled={disabled || left <= 0}
                    onClick={() => setPicker({ mode: "swap", index })}
                  >
                    <Repeat aria-hidden="true" />
                    Swap
                  </Button>
                  <div className="ms-auto flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-11 md:size-9"
                      data-action="up"
                      aria-label={`Move ${name} up`}
                      title={`Move ${name} up`}
                      disabled={disabled || index === 0}
                      onClick={() => {
                        onChange(moveAt(items, index, -1));
                        focusIn([control(item.exercise, "up"), control(item.exercise, "down")]);
                      }}
                    >
                      <ArrowUp aria-hidden="true" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-11 md:size-9"
                      data-action="down"
                      aria-label={`Move ${name} down`}
                      title={`Move ${name} down`}
                      disabled={disabled || index === items.length - 1}
                      onClick={() => {
                        onChange(moveAt(items, index, 1));
                        focusIn([control(item.exercise, "down"), control(item.exercise, "up")]);
                      }}
                    >
                      <ArrowDown aria-hidden="true" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-11 text-destructive hover:bg-destructive/10 hover:text-destructive md:size-9"
                      aria-label={removeLabel}
                      title={removeLabel}
                      disabled={disabled || (done > 0 && left <= 0)}
                      onClick={() => {
                        onChange(removeAt(items, index, logged));
                        // the row went (or its button turned off): the next
                        // row's Swap, else the one before, else Add
                        const after = items[index + 1];
                        const before = items[index - 1];
                        focusIn(
                          [after, before]
                            .filter(Boolean)
                            .map((other) => control(other.exercise, "swap")),
                          addRef.current,
                        );
                      }}
                    >
                      <Trash2 aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      <div>
        <Button
          ref={addRef}
          type="button"
          variant="outline"
          className="h-11 w-full md:h-9 md:w-auto"
          disabled={disabled || full}
          onClick={() => setPicker({ mode: "add" })}
        >
          <Plus aria-hidden="true" />
          Add an exercise
        </Button>
        {full && (
          <p className="mt-1 mb-0 text-xs text-muted-foreground">
            A plan holds up to {MAX_ITEMS} exercises — remove one to add another.
          </p>
        )}
      </div>
      <ExercisePicker
        open={picker !== null}
        onOpenChange={(open) => {
          if (!open) setPicker(null);
        }}
        title={swapping ? `Swap ${nameOf(swapping)}` : "Add an exercise"}
        description={
          swapping
            ? swapDescription()
            : "Anything your equipment allows. It's added at the end; its sets, reps and weight come from your history, as the plan's do."
        }
        options={choices.options}
        unavailable={choices.unavailable}
        settings={settings}
        muscleFilter={!swapping}
        emptyText={swapping && !swapExercise ? "No matches for an exercise that isn't in your list." : undefined}
        onPick={pick}
        onCloseAutoFocus={(event) => {
          const key = focusRow.current;
          focusRow.current = null;
          if (!key) return; // cancelled: back to the opener
          event.preventDefault();
          focusIn([control(key, "swap"), control(key, "up"), control(key, "down")], addRef.current);
        }}
      />
    </div>
  );
}

PlanEditor.propTypes = {
  /** The plan's items (TrainingPlanItem[]), in order. */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      exercise: PropTypes.string.isRequired,
      sets: PropTypes.number.isRequired,
      name: PropTypes.string,
    }),
  ).isRequired,
  /** The catalog (TrainingExercise[]) from the Training layout. */
  exercises: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** TrainingSettings — the equipment decides what can be swapped in or added. */
  settings: PropTypes.object.isRequired,
  /** Called with the new items array. */
  onChange: PropTypes.func.isRequired,
  /** Every control off (a save in flight). */
  disabled: PropTypes.bool,
  /** `{exercise key: sets logged}` during a session. */
  logged: PropTypes.objectOf(PropTypes.number),
  /** The plan's units (default: the settings'). */
  units: PropTypes.string,
  /** By exercise key: `{getting, failed, notices}` for an exercise brought in (W3). */
  notes: PropTypes.objectOf(
    PropTypes.shape({
      getting: PropTypes.bool,
      failed: PropTypes.bool,
      notices: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ),
};

export default PlanEditor;
